// Shared types for the placement-chrome preview layer.
//
// "Chrome" is the platform UI a placement draws over (Reels, Stories,
// YouTube) or around (IG Feed, FB Feed, Discover, Display) an ad
// creative. Rendering it around our own previews lets an operator judge
// a creative the way a viewer will actually see it — how much of the
// frame the caption block eats, whether the CTA collides with the
// headline, whether the right rail sits on top of a product.
//
// Two platform families are modelled, because the same canvas shape
// runs in completely different furniture depending on where it's
// bought: a 9:16 creative is Instagram Reels on Meta and YouTube Shorts
// on Google. Which family a preview uses is decided by the ad's own
// platformFormat (see familyForPlatformFormat below), never by shape.
//
// Preview-only. None of this is ever composited into a render.

// Canvas shapes we produce creatives for. Mirrors the aspectRatio
// strings the backend stores on Ad.aspectRatio (services/platformFormats.js).
export type AdRatio = '9:16' | '4:5' | '1:1' | '1.91:1' | '16:9';

// Ad platform a placement belongs to. Mirrors the `platform` field on
// services/platformFormats.js entries.
export type ChromeFamily = 'meta' | 'google';

// What a caller is asking to see. 'any' is an explicit opt-in to every
// platform's placements at once — used by Title Studio, which previews a
// brand-level titling spec rather than a specific ad and so has no
// platform to scope to. Everything else scopes to one family.
export type ChromeScope = ChromeFamily | 'any';

// A single placement overlay. 'none' renders the bare creative.
export type ChromeKind =
  | 'none'
  // Meta
  | 'reels'
  | 'stories'
  | 'ig-feed'
  | 'fb-feed'
  // Google — Performance Max distributes ONE creative across YouTube,
  // Display, Discover, Gmail, Search and Maps, and the operator cannot
  // pick the placement. So a PMax asset has several real homes rather
  // than one, and the selector offers all of them for a given shape the
  // same way it offers Reels vs Stories on Meta.
  //
  // Maps is deliberately not modelled: it collapses the creative to a
  // pin card that says nothing useful about the artwork.
  | 'yt-shorts'
  | 'yt-instream'
  | 'yt-infeed'
  | 'discover'
  | 'gdn-display'
  | 'gmail';

// Where the chrome sits relative to the creative. Overlay chromes are
// drawn ON the media (full-screen placements); card chromes wrap it in
// surrounding feed furniture and never cover the creative itself.
export type ChromePlacementModel = 'overlay' | 'card';

// Identity shown in the chrome. Sourced from the active brand — a real
// connected IG handle when one exists, else a slug of the brand name.
// Everything else in the chrome (caption body, like counts) is dummy
// copy by design: it's scaffolding, not a claim about performance.
export type ChromeIdentity = {
  handle:      string;        // without the leading @
  name:        string;
  logoUrl?:    string | null;
  // Destination shown in the Stories link sticker and the FB Feed link
  // row. Bare host, no scheme — "loremipsum.com".
  linkDomain?: string | null;
  // CTA label. Falls back to "Shop now" when the ad doesn't carry one.
  ctaText?:    string | null;
};

// Grid tiles get a reduced chrome: the structural elements that convey
// placement (scrims, rail, CTA bar, card header) minus the fine text
// that turns to mush below ~200px wide.
export type ChromeDensity = 'full' | 'compact';

export type ChromeOption = {
  kind:  ChromeKind;
  label: string;
};

// Normalizes whatever ratio string a call site has into an AdRatio.
// Accepts the backend's canonical strings plus a numeric fallback so
// surfaces that only know pixel dimensions still resolve.
export function normalizeRatio(input: string | null | undefined, width?: number | null, height?: number | null): AdRatio {
  const raw = String(input || '').trim();
  if (raw === '9:16' || raw === '4:5' || raw === '1:1' || raw === '1.91:1' || raw === '16:9') return raw;

  // 5:4 is a crop-time intermediate, never a delivered placement — it
  // sits between 1:1 and 1.91:1, and Feed is the only surface that
  // takes it. Treat as feed-shaped.
  if (raw === '5:4') return '1:1';

  const w = Number(width)  || 0;
  const h = Number(height) || 0;
  if (w > 0 && h > 0) return ratioFromDimensions(w, h);
  return '1:1';
}

// Nearest canonical placement shape for a pixel size. Used where an ad
// row carries width/height but no aspectRatio string.
export function ratioFromDimensions(width: number, height: number): AdRatio {
  const r = width / height;
  const candidates: Array<[AdRatio, number]> = [
    ['9:16',   9 / 16],
    ['4:5',    4 / 5],
    ['1:1',    1],
    ['1.91:1', 1.91],
    ['16:9',   16 / 9]
  ];
  let best: AdRatio = '1:1';
  let bestDelta = Infinity;
  for (const [name, value] of candidates) {
    const delta = Math.abs(Math.log(r / value));
    if (delta < bestDelta) { bestDelta = delta; best = name; }
  }
  return best;
}

// Numeric width/height pair for a ratio — feeds the CSS aspect-ratio on
// the chrome's media box.
export function ratioDimensions(ratio: AdRatio): { w: number; h: number } {
  switch (ratio) {
    case '9:16':   return { w: 1080, h: 1920 };
    case '4:5':    return { w: 1080, h: 1350 };
    case '1.91:1': return { w: 1200, h: 628 };
    case '16:9':   return { w: 1920, h: 1080 };
    case '1:1':
    default:       return { w: 1080, h: 1080 };
  }
}

const CARD_CHROMES: ChromeKind[] = [
  'ig-feed', 'fb-feed',
  'discover', 'gdn-display', 'gmail', 'yt-infeed'
];

export function chromePlacementModel(kind: ChromeKind): ChromePlacementModel {
  return CARD_CHROMES.includes(kind) ? 'card' : 'overlay';
}

// Ad.platformFormat → which platform's furniture to draw.
//
// Mirrors platformForFormat() in services/platformFormats.js, including
// its prefix fallback, so a format key this build has never heard of
// still lands in the right family. Returns null when the caller has no
// platformFormat to go on (Title Studio previews a brand-level titling
// spec, not a specific ad) — callers treat that as "offer both".
export function familyForPlatformFormat(platformFormat: string | null | undefined): ChromeFamily | null {
  const pf = String(platformFormat || '');
  if (!pf) return null;
  if (pf.startsWith('meta_')) return 'meta';
  // pmax_* and google_* (Demand Gen, Shorts) are all Google inventory.
  if (pf.startsWith('pmax_') || pf.startsWith('google_')) return 'google';
  return null;
}
